import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  Home as HomeIcon,
  People as PeopleIcon,
  Build as MaintenanceIcon,
  AttachMoney as MoneyIcon,
  CalendarMonth as CalendarIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../../lib/store';
import StatCard, { StatCardSkeleton } from '../../components/data/StatCard';
import ResponsiveContainer from '../../components/layout/ResponsiveContainer';
import { propertiesApi, profilesApi, maintenanceApi, contractsApi } from '../../../lib/api';
import { formatDate } from '../../../lib/dateUtils';

interface DashboardStats {
  totalProperties: number;
  occupiedProperties: number;
  totalTenants: number;
  pendingMaintenance: number;
  monthlyRevenue: number;
  occupancyRate: number;
}

interface TenantContract {
  id: string;
  property_id: string;
  start_date: string;
  end_date: string;
  rent_amount: number;  // Field name from database
  status: string;
  property: {
    id: string;
    title: string;
    address: string;
    city: string;
    property_code: string;
  } | null;
}

export default function DashboardHome() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation(['dashboard', 'common', 'properties']);
  const { user } = useAppStore();

  console.log('[DashboardHome] Rendering, user:', user?.email, 'role:', user?.role);

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [tenantContracts, setTenantContracts] = useState<TenantContract[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isTenant = user?.role === 'tenant';

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // If user is a tenant, load their contracts instead of stats
        if (isTenant && user?.id) {
          const contractsRes = await contractsApi.getByTenant(user.id);
          if (contractsRes.error) throw new Error(contractsRes.error.message);

          // Filter to active contracts only
          const activeContracts = (contractsRes.data || []).filter(
            (c: TenantContract) => c.status === 'active'
          );
          setTenantContracts(activeContracts);
        } else {
          // Non-tenant: load dashboard stats (with role-based filtering)
          const [summaryRes, tenantsRes, maintenanceRes] = await Promise.all([
            propertiesApi.getDashboardSummary(),
            profilesApi.getTenants(), // Uses role-based filtering - owners see only their tenants
            maintenanceApi.getRequests({ status: 'pending' })
          ]);

          if (summaryRes.error) throw new Error(summaryRes.error.message);
          if (tenantsRes.error) throw new Error(tenantsRes.error.message);
          if (maintenanceRes.error) throw new Error(maintenanceRes.error.message);

          const summary = summaryRes.data || {
            total_properties: 0,
            available: 0,
            occupied: 0,
            maintenance: 0,
            total_monthly_rent: 0,
            active_contracts: 0,
          };

          const totalProperties = Number(summary.total_properties) || 0;
          const occupied = Number(summary.occupied) || 0;
          const monthlyRevenue = Number(summary.total_monthly_rent) || 0;
          const pendingMaintenance = (maintenanceRes.data || []).length;
          const totalTenants = (tenantsRes.data || []).length;
          const occupancyRate = totalProperties > 0 ? Math.round((occupied / totalProperties) * 100) : 0;

          setStats({
            totalProperties,
            occupiedProperties: occupied,
            totalTenants,
            pendingMaintenance,
            monthlyRevenue,
            occupancyRate,
          });
        }
      } catch (err) {
        console.error('[Dashboard] loadData error:', err);
        setErrorMessage(err instanceof Error ? err.message : t('common:somethingWentWrong'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [t, isTenant, user?.id]);

  // Helper to calculate days until contract expiration
  const getDaysUntilExpiration = (endDate: string): number => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Helper to get expiration status color
  const getExpirationChipColor = (daysLeft: number): 'error' | 'warning' | 'success' => {
    if (daysLeft <= 30) return 'error';
    if (daysLeft <= 90) return 'warning';
    return 'success';
  };

  return (
    <ResponsiveContainer>
      {/* Header */}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
          }}
        >
          {t('welcome', { name: user?.first_name })}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' },
          }}
        >
          {isTenant ? t('tenantSubtitle', { defaultValue: 'View your rented properties and contract details' }) : t('subtitle')}
        </Typography>
      </Box>

      {/* Tenant View: Show rented properties with expiration dates */}
      {isTenant ? (
        <Box>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {t('myRentedProperties', { defaultValue: 'My Rented Properties' })}
          </Typography>

          {loading ? (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {[1, 2].map((i) => (
                <Grid item xs={12} md={6} key={i}>
                  <StatCardSkeleton />
                </Grid>
              ))}
            </Grid>
          ) : tenantContracts.length === 0 ? (
            <Card sx={{ p: 4, textAlign: 'center' }}>
              <HomeIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                {t('noRentedProperties', { defaultValue: 'No rented properties found' })}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('noActiveContracts', { defaultValue: 'You do not have any active rental contracts at the moment.' })}
              </Typography>
            </Card>
          ) : (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {tenantContracts.map((contract) => {
                const daysLeft = getDaysUntilExpiration(contract.end_date);
                const chipColor = getExpirationChipColor(daysLeft);

                return (
                  <Grid item xs={12} md={6} key={contract.id}>
                    <Card
                      sx={{
                        height: '100%',
                        transition: 'box-shadow 0.2s',
                        '&:hover': {
                          boxShadow: theme.shadows[4],
                        },
                      }}
                    >
                      <CardContent>
                        {/* Property Title */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                          <HomeIcon sx={{ color: 'primary.main', mr: 1.5, mt: 0.5 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                              {contract.property?.title || t('properties:untitled', { defaultValue: 'Untitled Property' })}
                            </Typography>
                            {contract.property?.property_code && (
                              <Typography variant="caption" color="text.secondary">
                                {contract.property.property_code}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {/* Address */}
                        {contract.property?.address && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, color: 'text.secondary' }}>
                            <LocationIcon sx={{ fontSize: 18, mr: 1 }} />
                            <Typography variant="body2">
                              {contract.property.address}
                              {contract.property.city && `, ${contract.property.city}`}
                            </Typography>
                          </Box>
                        )}

                        {/* Contract Dates */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <CalendarIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(contract.start_date)} - {formatDate(contract.end_date)}
                          </Typography>
                        </Box>

                        {/* Expiration Status */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Chip
                            label={
                              daysLeft > 0
                                ? t('daysUntilExpiration', { count: daysLeft, defaultValue: `${daysLeft} days remaining` })
                                : t('contractExpired', { defaultValue: 'Contract expired' })
                            }
                            color={chipColor}
                            size="small"
                            sx={{ fontWeight: 500 }}
                          />
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {contract.rent_amount?.toLocaleString()} {t('common:currency')}
                            <Typography component="span" variant="caption" color="text.secondary">
                              /{t('common:month', { defaultValue: 'month' })}
                            </Typography>
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      ) : (
        <>
          {/* Admin/Manager/Owner View: Stats Grid */}
          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
            {/* Total Properties */}
            <Grid item xs={12} sm={6} md={3}>
              {loading ? (
                <StatCardSkeleton />
              ) : (
                <StatCard
                  title={t('totalProperties')}
                  value={stats?.totalProperties || 0}
                  icon={<HomeIcon />}
                  color="primary"
                />
              )}
            </Grid>

            {/* Occupied Properties */}
            <Grid item xs={12} sm={6} md={3}>
              {loading ? (
                <StatCardSkeleton />
              ) : (
                <StatCard
                  title={t('occupied')}
                  value={stats?.occupiedProperties || 0}
                  icon={<HomeIcon />}
                  color="success"
                />
              )}
            </Grid>

            {/* Total Tenants */}
            <Grid item xs={12} sm={6} md={3}>
              {loading ? (
                <StatCardSkeleton />
              ) : (
                <StatCard
                  title={t('totalTenants')}
                  value={stats?.totalTenants || 0}
                  icon={<PeopleIcon />}
                  color="info"
                />
              )}
            </Grid>

            {/* Pending Maintenance */}
            <Grid item xs={12} sm={6} md={3}>
              {loading ? (
                <StatCardSkeleton />
              ) : (
                <StatCard
                  title={t('pendingMaintenance')}
                  value={stats?.pendingMaintenance || 0}
                  icon={<MaintenanceIcon />}
                  color="warning"
                />
              )}
            </Grid>
          </Grid>

          {/* Financial Stats */}
          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
            <Grid item xs={12} md={6}>
              {loading ? (
                <StatCardSkeleton />
              ) : (
                <StatCard
                  title={t('monthlyRevenue')}
                  value={`${stats?.monthlyRevenue.toLocaleString() || 0} ${t('common:currency')}`}
                  icon={<MoneyIcon />}
                  color="success"
                />
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              {loading ? (
                <StatCardSkeleton />
              ) : (
                <StatCard
                  title={t('occupancyRate')}
                  value={`${stats?.occupancyRate || 0}%`}
                  icon={<HomeIcon />}
                  color="primary"
                />
              )}
            </Grid>
          </Grid>
        </>
      )}

      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorMessage(null)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </ResponsiveContainer>
  );
}
